import {
  BLOCK_SYMBOL_TEXTURE_KEY,
  BLOCKED_MARKER,
  BUILDING_DAMAGE_MARKER,
  COMBAT_MARKER,
  CONSTRUCTION_MARKER,
  CONSTRUCTION_MARKER_KINDS,
  FLAME_TINTS,
  FORTRESS_GAUGE,
  LIVE_MARKER_TEXTURE_KEYS,
} from '../configs/liveFieldMarkers';
import {ABILITY_CAST_TEXTURE_KEYS} from '../configs/abilityCastAnimation';
import {GLOW_THREAD_TEXTURE_KEYS} from '../configs/glowingThreads';
import {MELEE_TEXTURE_KEYS} from '../configs/meleeAnimation';
import {SIZE_RECRUITMENT_TEXTURE_KEYS} from '../configs/sizeRecruitmentMarker';
import {ensureAbilityCastTextures} from '../utils/effects/abilityCastTextures';
import {ensureGlowThreadTextures} from '../utils/effects/glowThreadTextures';
import {
  resolveFlamePose,
  resolveFortressPipPositions,
  resolveHammerPose,
  resolveHammerSparkPose,
  resolveHourglassRotation,
  resolveMarkerSeed,
  resolvePulseShare,
  resolveRisingPose,
  resolveSharedFadeShare,
  resolveShimmer,
  resolveSparkPose,
  resolveStrikeShare,
} from '../utils/effects/liveMarkerPoses';
import {ensureLiveMarkerTextures} from '../utils/effects/liveMarkerTextures';
import {ensureMeleeTextures} from '../utils/effects/meleeEffectTextures';
import {ensureSizeRecruitmentTextures} from '../utils/effects/sizeRecruitmentTextures';

const TWO_PI = Math.PI * 2;
const SHADOW_TINT = 0x120904;

/**
 * ЖИВЫЕ МАРКЕРЫ СТРОЕНИЙ (см. configs/liveFieldMarkers). Маркер — контейнер:
 * снаружи он ведет себя как прежний спрайт (setDepth, setAlpha, destroy, твин
 * мигания), а внутри него огонь, дым и искры.
 *
 * Поле пересоздает маркеры на каждое обновление стора, поэтому все движение
 * считается от часов сцены: новый маркер продолжает с того же кадра. Контейнер
 * принадлежит ячейке и убирается вместе с ее маркерами; здесь он просто перестает
 * обновляться.
 */
export default class LiveFieldMarkers {
  /**
   * @param {Phaser.Scene} scene
   * @param {Object} [options]
   * @param {number} [options.linearFilter] — режим фильтрации LINEAR для текстур
   */
  constructor(scene, {linearFilter} = {}) {
    this.scene = scene;
    this.linearFilter = linearFilter;
    this.markers = new Set();
    this.isListening = false;
    this.tick = this.tick.bind(this);
  }

  ensureTextures() {
    const options = {linearFilter: this.linearFilter};
    ensureLiveMarkerTextures(this.scene, options);
    ensureSizeRecruitmentTextures(this.scene, options);
    ensureAbilityCastTextures(this.scene, options);
    ensureMeleeTextures(this.scene, options);
    ensureGlowThreadTextures(this.scene, options);
  }

  now() {
    return this.scene?.time?.now ?? 0;
  }

  addImage(children, x, y, textureKey, sizePx) {
    const image = this.scene.add.image(x, y, textureKey);
    image.baseScale = sizePx / (image.width || 1);
    image.setScale(image.baseScale);
    children.push(image);
    return image;
  }

  // Язык пламени из двух слоев: темно-красный снаружи, светлый внутри. Низ стоит
  // на месте, дрожит верх
  addFlame(children, y, sizePx, innerShare) {
    const addTongue = (share, tintBottom, tintTop) => {
      const tongue = this.addImage(children, 0, y, LIVE_MARKER_TEXTURE_KEYS.FLAME, sizePx * share);
      tongue.setOrigin(0.5, 0.92);
      tongue.setTint(tintTop, tintTop, tintBottom, tintBottom);
      return tongue;
    };
    return {
      outer: addTongue(1, FLAME_TINTS.outerBottom, FLAME_TINTS.outerTop),
      inner: addTongue(innerShare, FLAME_TINTS.innerBottom, FLAME_TINTS.innerTop),
    };
  }

  updateFlame({outer, inner}, now, seed) {
    [[outer, seed], [inner, seed + 0.43]].forEach(([tongue, tongueSeed]) => {
      const pose = resolveFlamePose(now, tongueSeed);
      tongue.setScale(tongue.baseScale * pose.scaleX, tongue.baseScale * pose.scaleY);
      tongue.setRotation(pose.rotation);
    });
  }

  addRising(children, config, textureKey, hexSize) {
    return Array.from({length: config.count}, () => {
      const image = this.addImage(children, 0, 0, textureKey, hexSize * config.sizeFactor);
      image.setTint(config.tint);
      image.setAlpha(0);
      return image;
    });
  }

  updateRising(images, config, now, seed, hexSize, {baseY = 0, maxAlpha = 1, grow = 0} = {}) {
    images.forEach((image, index) => {
      const {share, alpha, sway} = resolveRisingPose(now, seed, index, images.length, config.periodMs);
      image.setPosition(hexSize * config.spreadFactor * sway, baseY - hexSize * config.riseFactor * share);
      image.setAlpha(maxAlpha * alpha);
      image.setScale(image.baseScale * (1 + grow * share));
    });
  }

  addHalo(children, config, hexSize) {
    const halo = this.addImage(children, 0, 0, SIZE_RECRUITMENT_TEXTURE_KEYS.HALO, hexSize * config.sizeFactor);
    halo.setTint(config.tint);
    return halo;
  }

  updateHalo(halo, config, now, seed) {
    const share = resolvePulseShare(now, config.pulseMs, seed);
    halo.setAlpha(config.minAlpha + (config.maxAlpha - config.minAlpha) * share);
    halo.setScale(halo.baseScale * (0.94 + 0.12 * share));
  }

  register(x, y, depth, children, update) {
    const container = this.scene.add.container(x, y, children);
    container.setDepth(depth);
    const marker = {container, update};
    this.markers.add(marker);
    update(this.now());
    if (!this.isListening && this.scene?.events?.on) {
      this.scene.events.on('update', this.tick);
      this.isListening = true;
    }
    return container;
  }

  /**
   * Повреждение строения: медальон в углях, живое пламя, дым и искры
   * @param {Object} params
   * @param {number} params.x
   * @param {number} params.y
   * @param {number} params.hexSize
   * @param {number} params.depth
   * @returns {Phaser.GameObjects.Container}
   */
  createDamageMarker({x, y, hexSize, depth}) {
    this.ensureTextures();
    const {plateSizeFactor, halo: haloConfig, flame: flameConfig, embers: embersConfig, smoke: smokeConfig} = BUILDING_DAMAGE_MARKER;
    const seed = resolveMarkerSeed(x, y);
    const baseY = hexSize * flameConfig.baseYFactor;
    const children = [];

    const halo = this.addHalo(children, haloConfig, hexSize);
    const smoke = this.addRising(children, smokeConfig, ABILITY_CAST_TEXTURE_KEYS.PUFF, hexSize);
    this.addImage(children, 0, 0, LIVE_MARKER_TEXTURE_KEYS.EMBER_PLATE, hexSize * plateSizeFactor);
    const flame = this.addFlame(children, baseY, hexSize * flameConfig.sizeFactor, flameConfig.innerShare);
    const embers = this.addRising(children, embersConfig, MELEE_TEXTURE_KEYS.GLOW, hexSize);

    return this.register(x, y, depth, children, (now) => {
      this.updateHalo(halo, haloConfig, now, seed);
      this.updateFlame(flame, now, seed);
      this.updateRising(smoke, smokeConfig, now, seed, hexSize, {maxAlpha: smokeConfig.alpha, grow: 0.7});
      this.updateRising(embers, embersConfig, now, seed, hexSize, {baseY});
    });
  }

  /**
   * Блокировка: красная печать со знаком block.png, вокруг вращаются дуги, знак
   * светится как неон
   * @param {Object} params — как у createDamageMarker
   * @returns {Phaser.GameObjects.Container}
   */
  createBlockedMarker({x, y, hexSize, depth}) {
    this.ensureTextures();
    const {plateSizeFactor, halo: haloConfig, arcs: arcsConfig, symbol: symbolConfig} = BLOCKED_MARKER;
    const seed = resolveMarkerSeed(x, y);
    const children = [];

    const halo = this.addHalo(children, haloConfig, hexSize);
    this.addImage(children, 0, 0, LIVE_MARKER_TEXTURE_KEYS.BLOCK_PLATE, hexSize * plateSizeFactor);
    const arcs = this.addImage(children, 0, 0, LIVE_MARKER_TEXTURE_KEYS.BLOCK_ARCS, hexSize * arcsConfig.sizeFactor);
    arcs.setTint(arcsConfig.tint);
    arcs.setAlpha(arcsConfig.alpha);
    const symbolSize = hexSize * symbolConfig.sizeFactor;
    const glow = this.addImage(children, 0, 0, BLOCK_SYMBOL_TEXTURE_KEY, symbolSize * symbolConfig.glowScale);
    glow.setTintFill(symbolConfig.glowTint);
    const symbol = this.addImage(children, 0, 0, BLOCK_SYMBOL_TEXTURE_KEY, symbolSize);
    symbol.setTintFill(symbolConfig.tint);

    return this.register(x, y, depth, children, (now) => {
      this.updateHalo(halo, haloConfig, now, seed);
      arcs.setRotation(((now / arcsConfig.periodMs + seed) % 1) * TWO_PI);
      const share = resolvePulseShare(now, symbolConfig.pulseMs, seed);
      symbol.setAlpha(symbolConfig.minAlpha + (1 - symbolConfig.minAlpha) * share);
      glow.setAlpha(0.45 + 0.55 * share);
    });
  }

  /**
   * Действие строительства на ячейке (см. CONSTRUCTION_MARKER): рука на медальоне,
   * молоток в ней бьет, у плана переворачиваются песочные часы. Мигание, удары и пульс
   * идут от часов сцены и у всех значков в одной фазе, поэтому своей фазы по ячейке нет.
   *
   * Пока часы не дошли до container.revealAt, значка не видно: он ждет конца анимации
   * размещения строения. Это поле можно менять и у готового значка
   * @param {Object} params — как у createDamageMarker, плюс:
   * @param {string} params.kind — CONSTRUCTION_MARKER_KINDS
   * @returns {Phaser.GameObjects.Container}
   */
  createConstructionMarker({x, y, hexSize, depth, kind}) {
    this.ensureTextures();
    const config = CONSTRUCTION_MARKER.kinds[kind];
    const {hand: handConfig, hammer: hammerConfig, hourglass: hourglassConfig, frameSize, impactShare, fade, invalidPulse} = CONSTRUCTION_MARKER;
    // Фаза одна на все значки, поэтому своего сдвига по ячейке нет
    const seed = 0;
    const symbolSize = hexSize * CONSTRUCTION_MARKER.symbolSizeFactor;
    // Точка кадра знака в координатах маркера
    const toLocal = (value) => (value - frameSize / 2) * (symbolSize / frameSize);
    const children = [];

    const halo = this.addHalo(children, config.halo, hexSize);
    const plate = this.addImage(children, 0, 0, config.plateKey, hexSize * CONSTRUCTION_MARKER.plateSizeFactor);
    if (config.plateTint) {
      plate.setTint(config.plateTint);
    }
    const arcs = config.arcs
      ? this.addImage(children, 0, 0, LIVE_MARKER_TEXTURE_KEYS.BLOCK_ARCS, hexSize * config.arcs.sizeFactor)
      : null;
    if (arcs) {
      arcs.setTint(config.arcs.tint);
      arcs.setAlpha(config.arcs.alpha);
    }

    const outline = this.addImage(children, 0, 0, handConfig.textureKey, symbolSize * handConfig.outlineScale);
    outline.setTintFill(handConfig.outlineTint);
    const hand = this.addImage(children, 0, 0, handConfig.textureKey, symbolSize);
    hand.setTintFill(handConfig.tint);
    const addSymbolPart = ({textureKey, point}) => {
      const part = this.addImage(children, toLocal(point.x), toLocal(point.y), textureKey, symbolSize);
      part.setOrigin(point.x / frameSize, point.y / frameSize);
      part.setTintFill(config.symbolTint);
      return part;
    };

    let container = null;
    const updatePresence = (now) => {
      if (!container) {
        return;
      }
      const shown = now - container.revealAt;
      if (shown < 0) {
        container.setAlpha(0);
        return;
      }
      const reveal = Math.min(1, shown / fade.revealMs);
      if (kind === CONSTRUCTION_MARKER_KINDS.INVALID) {
        // Невалидное не мигает, а бьется как пульс: печать должна быть видна все время
        const {minScale, maxScale, durationMs, holdMs} = invalidPulse;
        container.setScale(minScale + (maxScale - minScale) * resolveSharedFadeShare(now, durationMs, holdMs));
        container.setAlpha(reveal);
        return;
      }
      container.setAlpha(reveal * resolveSharedFadeShare(now, fade.durationMs, fade.holdMs));
    };
    const finish = (update) => {
      container = this.register(x, y, depth, children, (now) => {
        update(now);
        updatePresence(now);
      });
      container.revealAt = 0;
      updatePresence(this.now());
      return container;
    };

    if (kind === CONSTRUCTION_MARKER_KINDS.PLAN) {
      const hourglass = addSymbolPart({textureKey: hourglassConfig.textureKey, point: hourglassConfig.center});
      return finish((now) => {
        this.updateHalo(halo, config.halo, now, seed);
        hourglass.setRotation(resolveHourglassRotation(now, config.flipMs, seed));
      });
    }

    const hammer = addSymbolPart({textureKey: hammerConfig.textureKey, point: hammerConfig.pivot});
    const impactX = toLocal(hammerConfig.impact.x);
    const impactY = toLocal(hammerConfig.impact.y);
    const flash = this.addImage(children, impactX, impactY, MELEE_TEXTURE_KEYS.GLOW, hexSize * config.flash.sizeFactor);
    flash.setTint(config.flash.tint);
    flash.setAlpha(0);
    const sparks = Array.from({length: config.sparks.count}, (_, index) => {
      const spark = this.addImage(children, impactX, impactY, MELEE_TEXTURE_KEYS.BURST, hexSize * config.sparks.sizeFactor);
      spark.setTint(config.sparks.tints[index % config.sparks.tints.length]);
      spark.setAlpha(0);
      return spark;
    });

    return finish((now) => {
      this.updateHalo(halo, config.halo, now, seed);
      if (arcs) {
        arcs.setRotation(((now / config.arcs.periodMs + seed) % 1) * TWO_PI);
      }
      const pose = resolveHammerPose(now, config.swingMs, seed, config.raiseAngle, config.recoilAngle);
      hammer.setRotation(pose.angle);
      const age = pose.share < impactShare ? pose.share / impactShare : 1;
      const jolt = 1 + config.jolt * (1 - age) ** 2;
      hand.setScale(hand.baseScale * jolt);
      outline.setScale(outline.baseScale * jolt);
      hammer.setScale(hammer.baseScale * jolt);
      flash.setAlpha((1 - age) ** 2);
      flash.setScale(flash.baseScale * (0.7 + 0.5 * age));
      sparks.forEach((spark, index) => {
        const sparkPose = resolveHammerSparkPose(age, pose.beat, index, sparks.length);
        const distance = hexSize * config.sparks.spreadFactor * sparkPose.reach;
        spark.setPosition(impactX + Math.cos(sparkPose.angle) * distance, impactY + Math.sin(sparkPose.angle) * distance);
        spark.setAlpha(age < 1 ? sparkPose.alpha : 0);
        spark.setRotation(sparkPose.angle);
      });
    });
  }

  /**
   * Отряд бьется или стреляет: золотой медальон со знаком действия и искрами
   * @param {Object} params — как у createDamageMarker, плюс:
   * @param {string} params.kind — COMBAT_MARKER_KINDS
   * @returns {Phaser.GameObjects.Container}
   */
  createCombatMarker({x, y, hexSize, depth, kind}) {
    this.ensureTextures();
    const config = COMBAT_MARKER.kinds[kind];
    const seed = resolveMarkerSeed(x, y);
    const children = [];

    const halo = this.addHalo(children, config.halo, hexSize);
    this.addImage(children, 0, 0, SIZE_RECRUITMENT_TEXTURE_KEYS.PLATE, hexSize * COMBAT_MARKER.plateSizeFactor);
    const symbol = this.addImage(children, 0, 0, config.symbolKey, hexSize * COMBAT_MARKER.symbol.sizeFactor);
    symbol.setTintFill(COMBAT_MARKER.symbol.tint);

    const addParticles = ({count, sizeFactor, tints, tint}, textureKey) => Array.from({length: count}, (_, index) => {
      const particle = this.addImage(children, 0, 0, textureKey, hexSize * sizeFactor);
      particle.setTint(tints ? tints[index % tints.length] : tint);
      particle.setAlpha(0);
      return particle;
    });
    const sparks = config.sparks ? addParticles(config.sparks, MELEE_TEXTURE_KEYS.BURST) : [];
    const bolts = config.bolts ? addParticles(config.bolts, MELEE_TEXTURE_KEYS.GLOW) : [];

    return this.register(x, y, depth, children, (now) => {
      this.updateHalo(halo, config.halo, now, seed);
      if (config.sparks) {
        symbol.setScale(symbol.baseScale * (1 + config.strikeScale * resolveStrikeShare(now, config.strikeMs, seed)));
        sparks.forEach((spark, index) => {
          const pose = resolveSparkPose(now, seed, index, sparks.length, config.sparks.periodMs);
          const distance = hexSize * config.sparks.spreadFactor * pose.reach;
          spark.setPosition(Math.cos(pose.angle) * distance, Math.sin(pose.angle) * distance);
          spark.setAlpha(pose.alpha);
          spark.setRotation(pose.angle);
        });
      }
      if (config.bolts) {
        bolts.forEach((bolt, index) => {
          const {share, alpha} = resolveRisingPose(now, seed, index, bolts.length, config.bolts.periodMs);
          bolt.setPosition(hexSize * (config.bolts.startFactor + config.bolts.travelFactor * share), 0);
          bolt.setAlpha(alpha);
          bolt.setScale(bolt.baseScale * (1.3 - 0.7 * share), bolt.baseScale * (0.7 - 0.25 * share));
        });
      }
    });
  }

  /**
   * Шкала крепости: шеврон слева на ячейке, сверху вниз — горящие угли полученного
   * урона, ниже стальные камни: сколько крепость еще выдержит
   *
   * @param {Object} params
   * @param {number} params.x — центр ячейки на высоте жетона
   * @param {number} params.y
   * @param {number} params.hexSize
   * @param {number} params.depth
   * @param {number} params.maxDamage — сколько камней на шкале
   * @param {number} params.appliedDamage — полученный урон
   * @param {number} params.pendingDamage — урон, который еще только будет
   * @returns {{container: Phaser.GameObjects.Container, pendingPips: Phaser.GameObjects.Container[]}}
   *   pendingPips мигают твином вызывающего, в такт остальным отложенным маркерам
   */
  createFortressGauge({x, y, hexSize, depth, maxDamage, appliedDamage, pendingDamage}) {
    this.ensureTextures();
    const {glow: glowConfig, intact, burning, embers: embersConfig} = FORTRESS_GAUGE;
    const gaugeX = x + hexSize * FORTRESS_GAUGE.xFactor;
    const gaugeY = y + hexSize * FORTRESS_GAUGE.yFactor;
    const seed = resolveMarkerSeed(x, y);
    const totalDamage = Math.min(appliedDamage + pendingDamage, maxDamage);
    const children = [];

    const glow = this.addImage(children, hexSize * FORTRESS_GAUGE.apexXFactor, 0, MELEE_TEXTURE_KEYS.GLOW, hexSize * glowConfig.sizeFactor);
    glow.setTint(glowConfig.tint);

    const chevron = this.addImage(children, 0, 0, LIVE_MARKER_TEXTURE_KEYS.FORTRESS_CHEVRON, 0);
    chevron.baseScale = (hexSize * FORTRESS_GAUGE.sizeFactor) / (chevron.height || 1);
    chevron.setScale(chevron.baseScale);

    const pipPositions = resolveFortressPipPositions(maxDamage, FORTRESS_GAUGE, hexSize);
    const pendingPips = [];
    const intactPips = [];
    const burningPips = [];
    pipPositions.forEach((position, index) => {
      const pipChildren = [];
      const damageNumber = index + 1;
      const pipShare = maxDamage > 1 ? index / (maxDamage - 1) : 0.5;
      if (damageNumber <= totalDamage) {
        const ember = this.addImage(pipChildren, 0, 0, MELEE_TEXTURE_KEYS.GLOW, hexSize * burning.glowSizeFactor);
        ember.setTint(burning.glowTint);
        const coal = this.addImage(pipChildren, 0, 0, GLOW_THREAD_TEXTURE_KEYS.NODE, hexSize * burning.sizeFactor);
        coal.setTint(burning.tint);
        const flame = this.addFlame(pipChildren, hexSize * burning.flameYFactor, hexSize * burning.flameSizeFactor, 0.6);
        burningPips.push({ember, flame, seed: seed + index * 0.17});
      } else {
        const outline = this.addImage(pipChildren, 0, 0, GLOW_THREAD_TEXTURE_KEYS.NODE, hexSize * intact.sizeFactor * intact.outlineFactor);
        outline.setTint(SHADOW_TINT);
        const gem = this.addImage(pipChildren, 0, 0, GLOW_THREAD_TEXTURE_KEYS.NODE, hexSize * intact.sizeFactor);
        gem.setTint(intact.tint);
        const shine = this.addImage(pipChildren, 0, 0, GLOW_THREAD_TEXTURE_KEYS.NODE, hexSize * intact.sizeFactor);
        shine.setTint(intact.shineTint);
        intactPips.push({shine, pipShare});
      }
      const pip = this.scene.add.container(position.x, position.y, pipChildren);
      children.push(pip);
      if (damageNumber > appliedDamage && damageNumber <= totalDamage) {
        pendingPips.push(pip);
      }
    });

    const embers = this.addRising(
      children,
      {...embersConfig, count: Math.min(embersConfig.maxCount, totalDamage)},
      MELEE_TEXTURE_KEYS.GLOW,
      hexSize,
    );

    const container = this.register(gaugeX, gaugeY, depth, children, (now) => {
      const glowShare = resolvePulseShare(now, glowConfig.pulseMs, seed);
      glow.setAlpha(glowConfig.minAlpha + (glowConfig.maxAlpha - glowConfig.minAlpha) * glowShare);
      burningPips.forEach((pip) => {
        this.updateFlame(pip.flame, now, pip.seed);
        pip.ember.setAlpha(0.55 + 0.45 * resolvePulseShare(now, 700, pip.seed));
      });
      intactPips.forEach(({shine, pipShare}) => {
        shine.setAlpha(resolveShimmer(now, seed, pipShare, intact.shimmerMs, intact.shimmerWidth));
      });
      this.updateRising(embers, embersConfig, now, seed, hexSize, {baseY: hexSize * 0.1});
      // Искры поднимаются вдоль шкалы: за счет разных фаз — от разных углей
      embers.forEach((image, index) => {
        const source = pipPositions[Math.floor((index * totalDamage) / Math.max(1, embers.length))];
        if (source) {
          image.setPosition(image.x + source.x, image.y + source.y);
        }
      });
    });
    return {container, pendingPips};
  }

  tick() {
    const now = this.now();
    this.markers.forEach((marker) => {
      if (marker.container.active === false) {
        this.markers.delete(marker);
        return;
      }
      marker.update(now);
    });
    if (this.markers.size === 0 && this.isListening) {
      this.scene?.events?.off?.('update', this.tick);
      this.isListening = false;
    }
  }

  /** Забыть все маркеры (разбор поля). Контейнеры убирают их ячейки */
  clear() {
    this.markers.clear();
    if (this.isListening) {
      this.scene?.events?.off?.('update', this.tick);
      this.isListening = false;
    }
  }
}
